import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import './UsersSettings.css';
import {
    GET_PROPERTIES,
    GET_USERS,
    GET_USER_BY_USERNAME_CASE_INSENSITIVE,
    CREATE_PROPERTY_PROPERTY_USER,
    GET_PROPERTY_PROPERTY_USERS_FOR_USER,
    DELETE_USER,
    UPDATE_PROPERTY_PROPERTY_USER_IS_DELETED,
    useLazyQuery,
    useQuery,
    useMutation,
    HARD_DELETE_PROPERTY_PROPERTY_USER,
    HARD_DELETE_USER,
    UPDATE_USER,
} from 'utils/graphql';
import { formatUTCDateTime } from 'utils/date.util';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import { capFirst } from 'utils/text.util';
import { isEmailValid } from 'utils/validation.util';
import { v4 as uuidv4 } from 'uuid';
import useToken from 'components/useToken';
import InfoModal from 'components/Modals/InfoModal/InfoModal';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import { ReactComponent as CheckIcon } from 'assets/icons/check_icon.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/error_icon.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/exclamation-triangle.svg';
import User from 'utils/user.util';
import { useTranslation } from 'react-i18next';

const UsersSettings = () => {
    const { t } = useTranslation();
    const { token } = useToken();
    const currentUser = User.getUser();

    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('name');
    const [orderByDirection, setOrderByDirection] = useState('asc');
    const [searchFilter, setSearchFilter] = useState('');

    const [showFormModal, setShowFormModal] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [inactive, setInactive] = useState(false);
    const [userPropertyIds, setUserPropertyIds] = useState([]);
    const [roles, setRoles] = useState([]);
    const [role, setRole] = useState();

    const [properties, setProperties] = useState([]);
    const [validated, setValidated] = useState(false);
    const [formErrors, setFormErrors] = useState({
        name: '',
        email: '',
        userPropertyIds: '',
        user: '',
    });
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    const [infoModalText, setInfoModalText] = useState('');
    const [infoModalOptions, setInfoModalOptions] = useState({});
    const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);

    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const { loading: isLoadingProperties } = useQuery(GET_PROPERTIES, {
        onCompleted: (data) => {
            setProperties(data.table.records.map((record) => record.property__c));
        },
    });

    const firstUpdate = useRef(true);

    const [getUsers] = useLazyQuery(GET_USERS, {
        fetchPolicy: 'cache-and-network',
    });

    const [createPropertyPropertyUser] = useMutation(CREATE_PROPERTY_PROPERTY_USER);
    const [updatePropertyPropertyUserIsDeleted] = useMutation(
        UPDATE_PROPERTY_PROPERTY_USER_IS_DELETED
    );
    const [deletePropertyUser] = useMutation(DELETE_USER);
    const [hardDeletePropertyPropertyUser] = useMutation(HARD_DELETE_PROPERTY_PROPERTY_USER);
    const [hardDeletePropertyUser] = useMutation(HARD_DELETE_USER);
    const [updatePropertyUser] = useMutation(UPDATE_USER);

    const [getUser] = useLazyQuery(GET_USER_BY_USERNAME_CASE_INSENSITIVE, {
        fetchPolicy: 'cache-and-network',
    });

    const [getPropertyPropertyUsers, { loading: isLoadingPropertyPropertyUsers }] = useLazyQuery(
        GET_PROPERTY_PROPERTY_USERS_FOR_USER,
        { fetchPolicy: 'cache-and-network' }
    );

    const setupRoles = () => {
        const roles = ['user', 'user_inventory'];
        if (currentUser.role === 'superadmin') {
            roles.push('admin', 'superadmin');
        } else if (currentUser.role === 'admin') {
            roles.push('admin');
        }
        setRoles(roles);
        setRole(roles[0]);
    };

    const columns = [
        {
            name: t('name'),
            sortable: true,
            id: 'name',
            selector: (row) => row.name,
        },
        {
            name: t('username'),
            sortable: true,
            id: 'username__c',
            selector: (row) => row.username__c,
        },
        {
            name: t('role'),
            sortable: true,
            id: 'role__c',
            selector: (row) => row.role__c,
        },
        {
            name: t('last_login'),
            sortable: true,
            id: 'last_login',
            selector: (row) =>
                row.last_login ? formatUTCDateTime(new Date(`${row.last_login}Z`)) : 'N/A',
        },
        {
            name: t('active'),
            sortable: true,
            id: 'inactive',
            selector: (row) => t((!row.inactive).toString()),
        },
        {
            name: t('manage'),
            sortable: false,
            id: 'manage',
            minWidth: '240px',
            selector: (row) => (
                <div className="d-flex align-items-center">
                    <Button
                        className="me-1"
                        variant="success"
                        disabled={isUserBeingDeleted()}
                        onClick={() => openFormModal(row)}
                    >
                        {t('edit')}
                    </Button>
                    <LoadingButton
                        isLoading={row.isDeleting}
                        isDisabled={isUserBeingDeleted()}
                        variant="danger"
                        onClick={() => {
                            setUserToDelete(row);
                            setIsDeleteConfirmationModalOpen(true);
                        }}
                    >
                        {t('delete')}
                    </LoadingButton>
                </div>
            ),
        },
    ];

    const datatableOnSort = (selectedColumn, sortDirection, sortedRows) => {
        if (selectedColumn && selectedColumn.id) {
            setOrderBy(selectedColumn.id);
            setOrderByDirection(sortDirection);
        }
    };

    const loadUsers = async () => {
        setIsLoading(true);
        const { data, error } = await getUsers({
            variables: {
                offset: (page - 1) * rowsPerPage,
                limit: rowsPerPage,
                orderBy,
                orderByDirection: orderByDirection.toUpperCase(),
                filter: searchFilter ? `%${searchFilter}%` : '%',
            },
        });
        if (data && data.table) {
            setTotalUsers(data.table.count);
            setUsers(
                data.table.records.map((record) => {
                    return { ...record.propertyuser__c, isDeleting: false };
                })
            );
            if (firstUpdate.current) {
                firstUpdate.current = false;
            }
        } else {
            console.log(error);
        }
        setIsLoading(false);
    };

    const isUserBeingDeleted = () => {
        return users.some((user) => user.isDeleting);
    };

    const openFormModal = (userToEdit = null) => {
        setUserToEdit(userToEdit);
        setShowFormModal(true);
        setFormValues(userToEdit);
    };

    const setFormValues = (user = null) => {
        setIsFormSubmitting(false);
        setValidated(false);
        if (user) {
            setName(user.name);
            setEmail(user.username__c);
            setInactive(user.inactive);
            setRole(user.role__c);
            getPropertyPropertyUsers({
                variables: {
                    propertyuser_id: user.sfid,
                },
                onCompleted: (data) => {
                    if (data.table) {
                        const propertyPropertyUsers = data.table.records.map(
                            (record) => record.property_propertyuser__c
                        );
                        setUserPropertyIds(
                            propertyPropertyUsers
                                .filter((propertyPropertyUsers) => !propertyPropertyUsers.isdeleted)
                                .map((propertyPropertyUser) => propertyPropertyUser.property__c)
                        );
                        setUserToEdit({
                            ...user,
                            propertyPropertyUsers,
                        });
                    }
                },
            });
        } else {
            setName('');
            setEmail('');
            setInactive(false);
            setUserPropertyIds([]);
            setRole(roles[0]);
        }
    };

    const validate = async () => {
        let _formErrors = {
            name: '',
            email: '',
            userPropertyIds: '',
            user: '',
        };
        let errors = false;

        if (!name) {
            _formErrors.name = 'name_empty_error';
            errors = true;
        }

        if (!email) {
            _formErrors.email = 'email_empty_error';
            errors = true;
        } else if (!isEmailValid(email)) {
            _formErrors.email = 'valid_email_error';
            errors = true;
        }

        if (!userPropertyIds || userPropertyIds.length === 0) {
            _formErrors.userPropertyIds = 'select_property_error';
            errors = true;
        }

        setFormErrors(_formErrors);
        if (errors) {
            setValidated(true);
            return false;
        }

        // on user creation and when email has been modified, checking if email is unique
        if (!userToEdit || userToEdit.username__c !== email) {
            const { data } = await getUser({
                variables: {
                    username: email,
                },
            });
            if (data.table.count > 0) {
                showUserFormError('user_with_email_exists');
                return false;
            }
        }
        return true;
    };

    const showInfoModal = (text, success = true) => {
        setInfoModalText(text);
        setInfoModalOptions({
            header: success ? (
                <CheckIcon fill="var(--bs-green)" />
            ) : (
                <ErrorIcon fill="var(--bs-red)" />
            ),
            center: true,
        });
        setInfoModalIsOpen(true);
    };

    const rollbackCreateUser = (userId, propertyPropertyUserIds) => {
        for (const id of propertyPropertyUserIds) {
            hardDeletePropertyPropertyUser({
                variables: {
                    id,
                },
            });
        }
        hardDeletePropertyUser({ variables: { id: userId } });
    };

    const createUser = async () => {
        setIsFormSubmitting(true);
        if (await validate()) {
            const date = formatUTCDateTime();
            const createUserResponse = await fetch(
                `${process.env.REACT_APP_PROXY_URL || ''}/createuser`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        name,
                        username: email,
                        inactive: true,
                        role,
                        sfid: uuidv4(),
                        createddate: date,
                    }),
                }
            );

            if (createUserResponse.ok) {
                const createUserData = await createUserResponse.json();
                if (createUserData.status !== 500) {
                    // creating  property propertyuser junctions
                    const createdPropertyPropertyUserIds = [];
                    for (const propertySfid of userPropertyIds) {
                        const { data: createPropertyUserData, errors } =
                            await createPropertyPropertyUser({
                                variables: {
                                    propertyuser: createUserData.user.sfid,
                                    property: propertySfid,
                                    createddate: date,
                                    sfid: uuidv4(),
                                },
                            });

                        if (errors || !createPropertyUserData.createRow.success) {
                            rollbackCreateUser(
                                createUserData.user.id,
                                createdPropertyPropertyUserIds
                            );
                            showUserFormError('user_creation_fail');
                            setIsFormSubmitting(false);
                            return;
                        }
                        createdPropertyPropertyUserIds.push(propertySfid);
                    }
                    sendUserActivationEmail(createUserData.user);
                    setShowFormModal(false);
                    showInfoModal('user_creation_success');
                    loadUsers();
                    return;
                }
            }
            showUserFormError('user_creation_fail');
            setIsFormSubmitting(false);
        } else {
            setIsFormSubmitting(false);
        }
    };

    const sendUserActivationEmail = (user) => {
        fetch(`${process.env.REACT_APP_PROXY_URL || ''}/sendactivationemail`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                email: user.username__c,
            }),
        }).catch((error) => {
            console.log({ error });
        });
    };

    const showUserFormError = (error) => {
        setFormErrors({
            name: '',
            email: '',
            userPropertyIds: '',
            user: error,
        });
        setValidated(true);
    };

    const rollbackEditUser = async (
        deletedPropertyPropertyUserIds = [],
        updatedPropertyPropertyUserIds = [],
        createdPropertyPropertyUserIds = []
    ) => {
        rollbackUpdatePropertyPropertyUserIsDeleted(deletedPropertyPropertyUserIds, false);
        rollbackUpdatePropertyPropertyUserIsDeleted(updatedPropertyPropertyUserIds, true);
        for (const idToDelete of createdPropertyPropertyUserIds) {
            hardDeletePropertyPropertyUser({
                variables: {
                    id: idToDelete,
                },
            });
        }
    };

    const editUser = async () => {
        setIsFormSubmitting(true);
        if (await validate()) {
            const date = formatUTCDateTime();

            const propertyPropertyUsersToDelete = userToEdit.propertyPropertyUsers.filter(
                (propertyPropertyUser) =>
                    !propertyPropertyUser.isdeleted &&
                    userPropertyIds.indexOf(propertyPropertyUser.property__c) === -1
            );

            const propertyPropertyUsersToUpdate = userToEdit.propertyPropertyUsers.filter(
                (propertyPropertyUser) =>
                    propertyPropertyUser.isdeleted &&
                    userPropertyIds.indexOf(propertyPropertyUser.property__c) !== -1
            );

            const propertyPropertyUserIdsToCreate = userPropertyIds.filter(
                (propertyId) =>
                    !userToEdit.propertyPropertyUsers.find(
                        (propertyPropertyUser) => propertyPropertyUser.property__c === propertyId
                    )
            );

            const deletedPropertyPropertyUserIds = [];
            for (const propertyPropertyUser of propertyPropertyUsersToDelete) {
                const { data: deletePropertyPropertyUserData, errors } =
                    await updatePropertyPropertyUserIsDeleted({
                        variables: {
                            id: propertyPropertyUser.id,
                            date,
                            isdeleted: true,
                        },
                    });

                if (
                    errors ||
                    !deletePropertyPropertyUserData ||
                    !deletePropertyPropertyUserData.updateRow.success
                ) {
                    rollbackEditUser(deletedPropertyPropertyUserIds);
                    showUserFormError('user_update_fail');
                    setIsFormSubmitting(false);
                    return;
                }
                deletedPropertyPropertyUserIds.push(propertyPropertyUser.id);
            }

            const updatedPropertyPropertyUserIds = [];
            for (const propertyPropertyUser of propertyPropertyUsersToUpdate) {
                const { data: updatePropertyPropertyUserData, errors } =
                    await updatePropertyPropertyUserIsDeleted({
                        variables: {
                            id: propertyPropertyUser.id,
                            date,
                            isdeleted: false,
                        },
                    });

                if (
                    errors ||
                    !updatePropertyPropertyUserData ||
                    !updatePropertyPropertyUserData.updateRow.success
                ) {
                    rollbackEditUser(
                        deletedPropertyPropertyUserIds,
                        updatedPropertyPropertyUserIds
                    );
                    showUserFormError('user_update_fail');
                    setIsFormSubmitting(false);
                    return;
                }
                updatedPropertyPropertyUserIds.push(propertyPropertyUser.id);
            }

            const createdPropertyPropertyUserIds = [];
            for (const propertySfid of propertyPropertyUserIdsToCreate) {
                const { data: createPropertyPropertyUserData, errors } =
                    await createPropertyPropertyUser({
                        variables: {
                            propertyuser: userToEdit.sfid,
                            property: propertySfid,
                            createddate: date,
                            sfid: uuidv4(),
                        },
                    });

                if (
                    errors ||
                    !createPropertyPropertyUserData ||
                    !createPropertyPropertyUserData.createRow.success
                ) {
                    rollbackEditUser(
                        deletedPropertyPropertyUserIds,
                        updatedPropertyPropertyUserIds,
                        createdPropertyPropertyUserIds
                    );
                    showUserFormError('user_update_fail');
                    setIsFormSubmitting(false);
                    return;
                }
                createdPropertyPropertyUserIds.push(
                    createPropertyPropertyUserData.createRow.property_propertyuser__c.id
                );
            }

            const { data: updatePropertyUserData, errors } = await updatePropertyUser({
                variables: {
                    id: userToEdit.id,
                    name,
                    email,
                    role,
                    date,
                    inactive,
                },
            });

            if (errors || !updatePropertyUserData || !updatePropertyUserData.updateRow.success) {
                rollbackEditUser(
                    deletedPropertyPropertyUserIds,
                    updatedPropertyPropertyUserIds,
                    createdPropertyPropertyUserIds
                );
                showUserFormError('user_update_fail');
                setIsFormSubmitting(false);
                return;
            }

            setShowFormModal(false);
            showInfoModal('user_update_success');
            loadUsers();
        } else {
            setIsFormSubmitting(false);
        }
    };

    const rollbackUpdatePropertyPropertyUserIsDeleted = (propertyPropertyUserIds, isdeleted) => {
        const date = formatUTCDateTime();
        for (const id of propertyPropertyUserIds) {
            updatePropertyPropertyUserIsDeleted({
                variables: {
                    id,
                    date,
                    isdeleted,
                },
            });
        }
    };

    const deleteUser = async () => {
        userToDelete.isDeleting = true;
        const getPropertyPropertyUsersResponse = await getPropertyPropertyUsers({
            variables: {
                propertyuser_id: userToDelete.sfid,
            },
        });

        if (!getPropertyPropertyUsersResponse?.errors) {
            const date = formatUTCDateTime();
            if (getPropertyPropertyUsersResponse.data.table.count > 0) {
                const propertyPropertyUserIdsToDelete =
                    getPropertyPropertyUsersResponse.data.table.records
                        .filter((record) => !record.property_propertyuser__c.isdeleted)
                        .map((record) => record.property_propertyuser__c.id);
                for (const id of propertyPropertyUserIdsToDelete) {
                    const deletePropertyPropertyUserResponse =
                        await updatePropertyPropertyUserIsDeleted({
                            variables: { id, date, isdeleted: true },
                        });
                    if (deletePropertyPropertyUserResponse.errors) {
                        rollbackUpdatePropertyPropertyUserIsDeleted(
                            propertyPropertyUserIdsToDelete,
                            false
                        );
                        showInfoModal('user_delete_fail', false);
                        userToDelete.isDeleting = false;
                        return;
                    }
                }
            }
            const deleteUserResponse = await deletePropertyUser({
                variables: {
                    id: userToDelete.id,
                    date,
                    email: `${userToDelete.username__c}_deleted_${uuidv4()}`,
                },
            });
            if (deleteUserResponse.errors) {
                rollbackUpdatePropertyPropertyUserIsDeleted([userToDelete.id], false);
                showInfoModal('user_delete_fail', false);
            } else {
                showInfoModal('user_delete_success');
                loadUsers();
            }
        } else {
            showInfoModal('user_delete_fail', false);
        }
        userToDelete.isDeleting = false;
    };

    useEffect(() => {
        loadUsers();
    }, [page, rowsPerPage, orderBy, orderByDirection]);

    useEffect(() => {
        if (firstUpdate.current) return;
        const timer = setTimeout(loadUsers, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [searchFilter]);

    useEffect(() => setValidated(false), [name, email, userPropertyIds]);

    useEffect(() => setupRoles(), []);

    return (
        <div className="user-settings">
            <Button
                className="w-100 mb-4"
                disabled={isUserBeingDeleted()}
                onClick={() => openFormModal()}
            >
                {t('create_user')}
            </Button>
            <div className="d-flex justify-content-end">
                <Form.Control
                    type="text"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    placeholder="Search"
                    className="search"
                />
            </div>
            <DataTable
                columns={columns}
                data={users}
                pagination
                paginationServer
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                paginationTotalRows={totalUsers}
                onChangePage={(page) => setPage(page)}
                onChangeRowsPerPage={(currentRowsPerPage) => setRowsPerPage(currentRowsPerPage)}
                progressPending={isLoading}
                progressComponent={
                    <div className="d-flex justify-content-center align-items-center my-4">
                        <Spinner></Spinner>
                    </div>
                }
                highlightOnHover
                persistTableHead
                sortServer
                onSort={(selectedColumn, sortDirection, sortedRows) =>
                    datatableOnSort(selectedColumn, sortDirection, sortedRows)
                }
                paginationComponentOptions={{ rowsPerPageText: t('rows_per_page') }}
            />
            <Modal show={showFormModal} onHide={() => setShowFormModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5>{t(userToEdit ? 'edit_user' : 'create_user')}</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <span className="required-field">*</span> {t('name')}
                            </Form.Label>
                            <Form.Control
                                isInvalid={validated && formErrors.name}
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            {formErrors.name && (
                                <Form.Control.Feedback type="invalid">
                                    {t(formErrors.name)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <span className="required-field">*</span> {t('email')}
                            </Form.Label>
                            <Form.Control
                                isInvalid={validated && formErrors.email}
                                type="text"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            {formErrors.email && (
                                <Form.Control.Feedback type="invalid">
                                    {t(formErrors.email)}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        {userToEdit && (
                            <Form.Check
                                className="mb-3"
                                type="checkbox"
                                label={t('inactive')}
                                checked={inactive}
                                onChange={(e) => setInactive(e.target.checked)}
                            />
                        )}
                        {isLoadingProperties || isLoadingPropertyPropertyUsers ? (
                            <div className="my-4 d-flex justify-content-center">
                                <Spinner></Spinner>
                            </div>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('properties')}
                                </Form.Label>
                                <Form.Control
                                    as="select"
                                    multiple
                                    isInvalid={validated && formErrors.userPropertyIds}
                                    value={userPropertyIds}
                                    onChange={(e) =>
                                        setUserPropertyIds(
                                            [].slice
                                                .call(e.target.selectedOptions)
                                                .map((item) => item.value)
                                        )
                                    }
                                >
                                    {properties.map((property, i) => (
                                        <option value={property.sfid} key={i}>
                                            {property.name}
                                        </option>
                                    ))}
                                </Form.Control>
                                {formErrors.userPropertyIds && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.userPropertyIds)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted">
                                    {t('ctrl_click_properties')}
                                </Form.Text>
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <span className="required-field">*</span> {t('role')}
                            </Form.Label>
                            <Form.Select
                                value={role}
                                onChange={(event) => setRole(event.target.value)}
                            >
                                {roles.map((role, i) => (
                                    <option value={role} key={i}>
                                        {capFirst(role)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        {validated && formErrors.user && (
                            <div
                                className="invalid-feedback mt-3 fw-bold"
                                style={{ display: formErrors.user ? 'block' : 'none' }}
                            >
                                {t(formErrors.user)}
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <LoadingButton
                        onClick={userToEdit ? editUser : createUser}
                        isLoading={isFormSubmitting}
                        isDisabled={isLoadingProperties || isLoadingPropertyPropertyUsers}
                    >
                        {t(userToEdit ? 'edit' : 'create')}
                    </LoadingButton>
                </Modal.Footer>
            </Modal>
            <InfoModal
                isOpen={infoModalIsOpen}
                setOpen={setInfoModalIsOpen}
                options={infoModalOptions}
            >
                {t(infoModalText)}
            </InfoModal>
            <ConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                setOpen={setIsDeleteConfirmationModalOpen}
                onConfirm={deleteUser}
                options={{
                    center: true,
                    header: <WarningIcon fill="var(--bs-yellow)" height={48} width={48} />,
                }}
            >
                <p>{t('delete_user_confirmation')}</p>
            </ConfirmationModal>
        </div>
    );
};

export default UsersSettings;
